import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import config from "../../../../../../config.json";
import {
  fetchRestoDataByUser,
  updateRestaurantLogo,
} from "../../../../../services/restaurantService";
import { DEFAULT_QR_PLACEMENT, placementToFormFields } from "../../../AppsMenu/Qrcode/qrCodeTemplates";
import { triggerPdfDownload, resolveQrPdfDownloadUrl } from "../../../AppsMenu/Qrcode/qrCodeDesignerUtils";
import { markOnboardingQrGenerated } from "../../../../../utils/onboardingTutorialStorage";
import { showWizardValidation } from "../wizardFeedback";
import { getPrimaryMenuLanguage, getPrimaryMenuName } from "./utils";
import {
  buildStaticMenuFilename,
  formatWizardSubmitError,
  getWizardSubmitIssues,
  prepareWizardDraftForSubmit,
  resolveWizardUploadFile,
  uploadWizardProductPhotos,
  uploadWizardSubcategoryBanners,
} from "../draftStorage";
import WizardLivePhone from "../WizardLivePhone";

const Step6Preview = ({
  draft,
  goPrev,
  onConfirmed,
  wizardPath,
  menuPhotoFile,
  menuPhotoDataUrl,
  menuPhotoPreview,
  restaurantLogoFile,
  restaurantLogoDataUrl,
  restaurantLogoPreview,
  productPhotos = {},
  subcategoryBanners = {},
  staticMenuFile,
  staticMenuDataUrl,
  staticMenuPreview,
  activeStep,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [qrChoice, setQrChoice] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const isStatic = wizardPath === "static";

  const menuName = getPrimaryMenuName(draft?.menu) || t("wizardPreviewUntitledMenu");
  const menuLang = getPrimaryMenuLanguage(draft?.menu);
  const products = Array.isArray(draft?.products) ? draft.products : [];
  const relations = draft?.relations || {};
  const productsCount = products.length;
  const categoriesCount = Array.isArray(draft?.categories) ? draft.categories.length : 0;
  const subcategoriesCount = Array.isArray(draft?.subcategories) ? draft.subcategories.length : 0;

  const assignedProductIds = useMemo(() => {
    const assigned = new Set();
    Object.values(relations.categoryProducts || {}).forEach((ids) => {
      (ids || []).forEach((id) => assigned.add(id));
    });
    Object.values(relations.subcategoryProducts || {}).forEach((ids) => {
      (ids || []).forEach((id) => assigned.add(id));
    });
    return assigned;
  }, [relations]);

  const unassignedCount = products.filter((p) => !assignedProductIds.has(p.tempId)).length;

  const { errors, warnings, canSubmit } = useMemo(
    () =>
      getWizardSubmitIssues(draft, t, {
        wizardPath,
        hasStaticFile: !!(staticMenuFile || staticMenuDataUrl || staticMenuPreview),
      }),
    [draft, t, wizardPath, staticMenuFile, staticMenuDataUrl, staticMenuPreview]
  );

  const goToQrDesigner = () => {
    navigate("/app-qrcode");
  };

  const generateAutoQr = async () => {
    const token = localStorage.getItem("tokenLogin");
    const restoData = await fetchRestoDataByUser();
    const resto = restoData?.restaurant ?? restoData;
    const tableQRCode = resto?.menuColors?.tableQRCode;
    const placementFields = placementToFormFields(DEFAULT_QR_PLACEMENT);

    const formData = new FormData();
    formData.append("templateType", "preset");
    formData.append("presetId", "classic");
    formData.append("qrLeftPercent", placementFields.qrLeftPercent);
    formData.append("qrTopPercent", placementFields.qrTopPercent);
    formData.append("qrSizePercent", placementFields.qrSizePercent);
    formData.append("domaineName", config.client);
    if (tableQRCode) {
      formData.append("tableQRCodeColor", tableQRCode);
    }

    const response = await axios.post(`${config.dev}/v2/restaurant/generateQRcode`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 200 && response.data?.pdfUrl) {
      markOnboardingQrGenerated();
      window.dispatchEvent(new CustomEvent("onboarding-tour:progress"));
      const fullUrl = resolveQrPdfDownloadUrl({
        pdfUrl: response.data.pdfUrl,
        pdfFilename: response.data.pdfFilename,
      });
      if (fullUrl) {
        await triggerPdfDownload(fullUrl, `qrcode-${Date.now()}.pdf`);
      }
    }
  };

  const submit = async () => {
    if (!canSubmit || submitting) return;

    if (qrChoice !== true && qrChoice !== false) {
      showWizardValidation(t, "wizardConfirmQrRequired");
      return;
    }

    setSubmitting(true);
    setError(null);
    setResult(null);

    try {
      const preparedDraft = prepareWizardDraftForSubmit({
        ...draft,
        wizardPath,
      });
      const payload = new FormData();
      payload.append("data", JSON.stringify(preparedDraft));

      const photoFile = await resolveWizardUploadFile({
        file: menuPhotoFile,
        dataUrl: menuPhotoDataUrl,
        previewUrl: menuPhotoPreview,
        defaultName: "menu-logo.webp",
        fallbackMime: "image/webp",
      });
      if (photoFile) {
        payload.append("file", photoFile);
      }

      if (isStatic) {
        const staticDefaultName = buildStaticMenuFilename(
          draft?.menu?.staticMenuMediaType,
          staticMenuFile?.type
        );
        const staticFile = await resolveWizardUploadFile({
          file: staticMenuFile,
          dataUrl: staticMenuDataUrl,
          previewUrl: staticMenuPreview,
          defaultName: staticDefaultName,
          fallbackMime:
            draft?.menu?.staticMenuMediaType === "pdf" ? "application/pdf" : "image/jpeg",
        });
        if (!staticFile) {
          setError(t("wizardConfirmErrorStaticFile"));
          setSubmitting(false);
          return;
        }
        payload.append("staticFile", staticFile);
      }

      const token = localStorage.getItem("tokenLogin");
      const res = await axios.post(`${config.dev}/v2/setup/start-now`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
      });

      if (!isStatic) {
        try {
          await uploadWizardProductPhotos({
            products: draft.products,
            productIds: res.data?.created?.products,
            productPhotos,
            token,
          });
        } catch (photoError) {
          console.error("Wizard product photo upload failed:", photoError);
        }
        try {
          await uploadWizardSubcategoryBanners({
            subcategories: draft.subcategories,
            subcategoryIds: res.data?.created?.subcategories,
            subcategoryBanners,
            token,
          });
        } catch (bannerError) {
          console.error("Wizard subcategory banner upload failed:", bannerError);
        }
      }

      try {
        const logoFile = await resolveWizardUploadFile({
          file: restaurantLogoFile,
          dataUrl: restaurantLogoDataUrl,
          previewUrl: restaurantLogoPreview,
          defaultName: "restaurant-logo.webp",
          fallbackMime: "image/webp",
        });
        if (logoFile) {
          await updateRestaurantLogo(logoFile);
        }
      } catch (logoError) {
        console.error("Wizard restaurant logo upload failed:", logoError);
      }

      setResult(res.data);
      if (typeof onConfirmed === "function") {
        onConfirmed(res.data?.menuId);
      }

      if (qrChoice === true) {
        try {
          await generateAutoQr();
        } catch (qrError) {
          console.error("Auto QR generation failed:", qrError);
        }
      }

      goToQrDesigner();
    } catch (e) {
      setError(formatWizardSubmitError(e?.response?.data || e, t));
    } finally {
      setSubmitting(false);
    }
  };

  const summaryItems = isStatic
    ? [{ icon: "fa-file-image", label: t("menuContentTypeStatic"), value: 1 }]
    : [
        { icon: "fa-hamburger", label: t("wizardPreviewStatProducts"), value: productsCount },
        { icon: "fa-folder-open", label: t("wizardPreviewStatCategories"), value: categoriesCount },
        { icon: "fa-layer-group", label: t("wizardPreviewStatSubcategories"), value: subcategoriesCount },
      ];

  const previewLogo =
    restaurantLogoPreview ||
    (isStatic && staticMenuPreview
      ? draft?.menu?.staticMenuMediaType === "pdf"
        ? null
        : staticMenuPreview
      : menuPhotoPreview);

  const stepCounter = isStatic ? t("wizardStepStaticPreview") : t("wizardStep7of9");

  return (
    <>
      <div className="mt-3 start-step-content wizard-preview-step">
        <div className="start-step-hero">
          <h2 className="start-step-title">
            <span className="step-badge">{isStatic ? "4" : "7"}</span>
            {t("wizardPreviewHeading")}
          </h2>
          <p className="start-step-subtitle">
            {isStatic ? t("wizardStaticPreviewSub") : t("wizardPreviewSub")}
          </p>
        </div>

        <div className="start-preview-layout">
          <section className="start-preview-phone" aria-label={t("wizardPreviewLive")}>
            <WizardLivePhone
              draft={draft}
              menuPhotoPreview={menuPhotoPreview}
              restaurantLogoPreview={restaurantLogoPreview}
              staticMenuPreview={staticMenuPreview}
              wizardPath={wizardPath}
              productPhotos={productPhotos}
              activeStep={activeStep}
              featured
            />
            <p className="wizard-preview-stage__hint">{t("wizardPreviewPhoneHint")}</p>
          </section>

          <div className="start-preview-panel">
            <section className="start-products-form" aria-label={t("wizardPreviewSummary")}>
              <div className="start-options-section-head">
                <span className="start-options-section-head__index">1</span>
                <h3 className="start-products-added__title">{t("wizardPreviewSummary")}</h3>
              </div>

              <div className="start-preview-menu">
                {previewLogo ? (
                  <img src={previewLogo} alt="" className="start-preview-menu__logo" />
                ) : (
                  <div className="start-preview-menu__logo is-empty" aria-hidden="true">
                    <i className={`fa ${isStatic ? "fa-file-pdf" : "fa-book-open"}`} />
                  </div>
                )}
                <div className="start-preview-menu__copy">
                  <span className="start-preview-menu__label">{t("wizardConfirmMenuLabel")}</span>
                  <strong className="start-preview-menu__name">{menuName}</strong>
                  <span className="start-preview-menu__lang">{menuLang.toUpperCase()}</span>
                </div>
              </div>

              <div className="start-preview-stats">
                {summaryItems.map((item) => (
                  <div key={item.label} className="start-preview-stat">
                    <i className={`fa ${item.icon}`} aria-hidden="true" />
                    <strong>{item.value}</strong>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>

              {!result && !isStatic && unassignedCount > 0 ? (
                <div className="preview-alert preview-alert--warning" role="status">
                  <i className="fa fa-exclamation-triangle" aria-hidden="true" />
                  <span>{t("wizardPreviewUnassignedWarning", { count: unassignedCount })}</span>
                </div>
              ) : null}

              {result ? (
                <div className="start-preview-status is-success">
                  <i className="fa fa-check-circle" aria-hidden="true" />
                  <div>
                    <strong>{t("wizardConfirmSuccessTitle")}</strong>
                    <p>{t("wizardConfirmSuccessSub")}</p>
                  </div>
                </div>
              ) : error ? (
                <div className="start-preview-status is-error">
                  <i className="fa fa-times-circle" aria-hidden="true" />
                  <div>
                    <strong>{t("wizardConfirmErrorTitle")}</strong>
                    <p>{error}</p>
                  </div>
                </div>
              ) : null}

              {!result && (errors.length > 0 || warnings.length > 0) ? (
                <ul className="confirm-issues-list">
                  {errors.map((issue) => (
                    <li key={issue} className="confirm-issues-list__item is-error">
                      <i className="fa fa-times-circle" aria-hidden="true" />
                      {issue}
                    </li>
                  ))}
                  {warnings.map((issue) => (
                    <li key={issue} className="confirm-issues-list__item is-warning">
                      <i className="fa fa-exclamation-triangle" aria-hidden="true" />
                      {issue}
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>

            <section className="start-products-added" aria-label={t("wizardConfirmQrPromptTitle")}>
              <div className="start-options-section-head">
                <span className="start-options-section-head__index">2</span>
                <h3 className="start-products-added__title">{t("wizardConfirmQrPromptTitle")}</h3>
              </div>

              {result ? (
                <Link to="/menu" className="confirm-qr-choice__menu-link">
                  {t("wizardConfirmViewMenus")}
                </Link>
              ) : (
                <>
                  <p className="start-options-hint">{t("wizardConfirmQrPromptSub")}</p>
                  <div
                    className="start-preview-qr"
                    role="radiogroup"
                    aria-label={t("wizardConfirmQrPromptTitle")}
                  >
                    <label className={`start-preview-qr__option${qrChoice === true ? " is-selected" : ""}`}>
                      <input
                        type="radio"
                        name="wizard-qr-choice"
                        checked={qrChoice === true}
                        onChange={() => setQrChoice(true)}
                        disabled={submitting}
                      />
                      <span>
                        <strong>{t("wizardConfirmQrYes")}</strong>
                      </span>
                    </label>
                    <label className={`start-preview-qr__option${qrChoice === false ? " is-selected" : ""}`}>
                      <input
                        type="radio"
                        name="wizard-qr-choice"
                        checked={qrChoice === false}
                        onChange={() => setQrChoice(false)}
                        disabled={submitting}
                      />
                      <span>
                        <strong>{t("wizardConfirmQrNo")}</strong>
                      </span>
                    </label>
                  </div>
                </>
              )}
            </section>
          </div>
        </div>
      </div>

      <div className="toolbar toolbar-bottom start-toolbar">
        <button
          className="btn btn-secondary sw-btn-prev me-1 start-btn-back"
          onClick={goPrev}
          disabled={submitting || !!result}
          type="button"
        >
          ← {t("back")}
        </button>
        <div className="wizard-step-counter">{stepCounter}</div>
        <button
          className="btn btn-success sw-btn-next ms-1 start-btn-next"
          onClick={submit}
          disabled={submitting || !canSubmit || !!result}
          type="button"
        >
          {submitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              {t("wizardConfirmSubmitting")}
            </>
          ) : (
            t("wizardConfirmSubmit")
          )}
        </button>
      </div>
    </>
  );
};

export default Step6Preview;
